import React from 'react';

/**
 * Catches a failed lazy-chunk load and reloads once, or offers a reload button.
 *
 * Every route in this app is now behind `React.lazy`, and the chunk filenames are
 * content-hashed. The standard failure is a deploy: a browser holding the old
 * `index.html` asks for a chunk name that no longer exists, the dynamic import
 * rejects, and the rejection propagates out of Suspense — React unmounts the
 * whole tree and leaves a blank white page. A reload fixes it every time, which
 * is the entire reason this exists: without it the funnel's paid traffic gets a
 * blank page and no way to know a refresh is all it needs.
 *
 * Because the reload is the only cure and it always works, a chunk error does it
 * for you — `RELOAD_KEY` in sessionStorage, holding the timestamp of the last
 * automatic reload, is what stops that becoming a loop when the new build is the
 * thing that's broken. A second failure inside `RELOAD_GUARD_MS` falls back to
 * the button. The window is short on purpose: the admin page lives in the hash,
 * so a genuinely broken chunk re-imports and fails again within a second or two
 * of the reload, while the next stale-deploy failure is minutes away at least
 * and deserves its own automatic reload.
 *
 * Only a module-load failure reloads. Everything else a child can throw — a
 * render bug in the page itself — gets the button, since reloading into the same
 * crash helps nobody.
 *
 * Deliberately not a general-purpose error boundary. It does not report or try
 * to render a partial UI — it fixes the one failure it knows how to fix, and
 * otherwise says what happened and gives them the one button that works.
 */
interface Props {
  children: React.ReactNode;
  /** Shown above the reload button. Keep it in the voice of the surface it wraps. */
  message?: string;
}

interface State {
  failed: boolean;
  /** A reload is already on its way; render nothing rather than flashing the prompt. */
  reloading: boolean;
}

const RELOAD_KEY = 'pf-chunk-reload';
const RELOAD_GUARD_MS = 30_000;

/**
 * Every engine words it differently: Firefox "error loading dynamically imported
 * module", Chrome "Failed to fetch dynamically imported module", Safari
 * "Importing a module script failed".
 */
function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return /dynamically imported module|module script failed/i.test(message);
}

/**
 * True at most once per `RELOAD_GUARD_MS`, and never when sessionStorage is
 * unavailable (Safari private mode throws) — without somewhere to record the
 * attempt there is nothing to break the loop, so the button it is.
 */
function claimReload(): boolean {
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_KEY));
    if (Number.isFinite(last) && Date.now() - last < RELOAD_GUARD_MS) return false;
    window.sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
    return true;
  } catch {
    return false;
  }
}

export class ChunkBoundary extends React.Component<Props, State> {
  state: State = { failed: false, reloading: false };

  static getDerivedStateFromError(): Partial<State> {
    return { failed: true };
  }

  componentDidCatch(error: unknown) {
    // Console only. There is no error-reporting sink on this origin, and a
    // failed chunk load is not something the API can be told about — the code
    // that would do the telling is what failed to load.
    console.error('[ChunkBoundary] failed to load a page chunk', error);
    if (isChunkLoadError(error) && claimReload()) {
      this.setState({ reloading: true });
      window.location.reload();
    }
  }

  render() {
    if (!this.state.failed) return this.props.children;
    if (this.state.reloading) return null;
    return (
      <div style={wrap}>
        <p style={text}>{this.props.message ?? 'Something went wrong loading this page.'}</p>
        <button type="button" style={button} onClick={() => window.location.reload()}>
          Reload
        </button>
      </div>
    );
  }
}

const wrap: React.CSSProperties = {
  minHeight: '60vh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 16,
  padding: 24,
  fontFamily: 'Inter, system-ui, sans-serif',
};
const text: React.CSSProperties = { margin: 0, fontSize: 14, color: '#5f5f56', textAlign: 'center' };
const button: React.CSSProperties = {
  border: 'none',
  borderRadius: 10,
  padding: '10px 20px',
  fontSize: 14,
  fontWeight: 600,
  color: '#fff',
  background: 'oklch(0.55 0.09 200)',
  cursor: 'pointer',
};
